// Utils
import { readOwnCommunicationNavigationContext } from '../../../../../../utils/communicationEditDraft';
import { isInternalPath } from '../../../../../../utils/internalPath';

/**
 * ¿A dónde vuelve el usuario después de guardar una edición de comunicación?
 *
 * Una sola función para los DOS caminos que existen —el guardado online
 * (`ActionUtils/saveData.ts`) y el encolado offline (`../action.ts`)—, que
 * hasta 2026-08-04 repetían la lógica y, sobre todo, repetían un literal
 * `'/reporting/historical-communications'` cableado a Informes. Con el wizard
 * fuera de Informes ese destino ya no existe, y tenerlo en un único sitio es
 * lo que impide que se arregle solo la mitad.
 *
 * Orden de resolución:
 *
 * 1. `returnTo` interno → ahí. Lo escribe quien abrió la edición (las
 *    pestañas de campaña mandan el suyo, así que la edición vuelve a la
 *    campaña y no a la vista global).
 * 2. Origen `workorder` con `workOrderId` → la OT padre, o su campaña si el
 *    `parentRoid` del contexto de sesión empieza por `Ca`. Sin cambios
 *    respecto al comportamiento anterior.
 * 3. Sin contexto propio y con padre conocido → el padre (OT o campaña).
 * 4. Por defecto → el detalle de la comunicación en Órdenes de Trabajo, cuyo
 *    botón "Atrás" ya devuelve a la tabla. Espejo exacto de Accidentes, que
 *    cae a `/work-orders/accidents/:orderId`.
 *
 * ⚠️ Llamar ANTES de limpiar el sessionStorage: el paso 2 lee
 * `COMMUNICATION_NAVIGATION_CONTEXT`.
 */
/** Padre de la comunicación según la selección de la edición (OT o campaña). */
export type EditCommunicationParent = { id?: string | null; isCampaign?: boolean } | null;

/**
 * El padre que da la selección de la pantalla (`storedDataOTSelected`) según la
 * pestaña de la edición: 1 = OT, 2 = campaña (`getModalKeyByTab`), 0 = sin
 * padre. Cubre también el cambio de padre hecho en la propia edición.
 */
export const parentFromSelection = (
  tabSelected: number,
  storedDataOTSelected: { id?: string | null } | null | undefined
): EditCommunicationParent =>
  tabSelected > 0 && storedDataOTSelected?.id
    ? { id: storedDataOTSelected.id, isCampaign: tabSelected === 2 }
    : null;

export function resolveEditCommunicationRedirect(
  formData: FormData,
  serverParent: EditCommunicationParent = null
): string {
  const returnTo = formData.get('returnTo')?.toString();
  if (returnTo && isInternalPath(returnTo)) return returnTo;

  const navigationOrigin = formData.get('navigationOrigin')?.toString();
  const workOrderId = formData.get('workOrderId')?.toString();

  if (navigationOrigin === 'workorder' && workOrderId) {
    // Solo el contexto de ESTA comunicación (`entityId` = la URL); el de otra
    // visita se ignora y no decide si se vuelve a una campaña (S2).
    const navContext = readOwnCommunicationNavigationContext(
      formData.get('communicationOrderId')?.toString()
    );
    const parentRoidStr = Array.isArray(navContext?.parentRoid)
      ? navContext?.parentRoid.join('.')
      : navContext?.parentRoid;
    if (parentRoidStr?.startsWith('Ca')) {
      return `/work-orders/campaign/${workOrderId}`;
    }
    return `/work-orders/${workOrderId}`;
  }

  const communicationOrderId = formData.get('communicationOrderId')?.toString();

  // Sin contexto PROPIO (el de otra visita se ignora, o no hay) se vuelve al
  // padre que da el servidor, en su árbol (campaña u OT): el detalle global
  // sacaba al usuario de la campaña (S2). Con contexto propio (la tabla), como
  // siempre: al detalle.
  if (
    serverParent?.id &&
    !readOwnCommunicationNavigationContext(communicationOrderId)
  ) {
    return serverParent.isCampaign
      ? `/work-orders/campaign/${serverParent.id}`
      : `/work-orders/${serverParent.id}`;
  }

  return communicationOrderId
    ? `/work-orders/communications/${communicationOrderId}`
    : '/work-orders/communications';
}
